<script>
	import { onMount } from 'svelte';
	import { goto, afterNavigate } from '$app/navigation';
	import { uiStore } from '$lib/stores/uiStore.js';
	import { authStore } from '$lib/stores/authStore.js';
	import { academyStore } from '$lib/stores/academyStore.js';
	import { api } from '$lib/utils/api.js';
	import { hasUnread, checkUnread } from '$lib/stores/announcementStore.js';
	import {
		hasUnread as hasUnreadInquiryReply,
		checkUnread as checkInquiryReplyUnread
	} from '$lib/stores/inquiryReplyStore.js';
	import { startUnreadPolling } from '$lib/utils/unreadPolling.js';
	import MenuIcon from '$lib/components/icons/MenuIcon.svelte';
	import UsersIcon from '$lib/components/icons/UsersIcon.svelte';
	$: isLoggedIn = $authStore.isLoggedIn;
	$: user = $authStore.user;
	$: isOAuthUser = user?.oauth_provider && user.oauth_provider !== 'email';

	$: academyName = $academyStore?.name || '';
	$: hasAcademy = !!$academyStore;

	onMount(() => {
		if ($authStore.isLoggedIn) {
			fetchAcademy();
		}
		// SPA에서 탭 복귀/창 포커스/60초 주기로 미확인 알림 갱신
		return startUnreadPolling([checkUnread, checkInquiryReplyUnread], () => $authStore.isLoggedIn);
	});

	// 로그인 상태 변경 시 학원 정보 및 알림 다시 가져오기
	$: if (isLoggedIn) {
		fetchAcademy();
		checkUnread();
		checkInquiryReplyUnread();
	}

	// 페이지 이동 시 학원 정보 및 미확인 알림 재조회 (SPA 네비게이션 대응)
	afterNavigate(() => {
		if (!$authStore.isLoggedIn) return;
		if (!$academyStore) fetchAcademy();
		checkUnread();
		checkInquiryReplyUnread();
	});

	async function fetchAcademy() {
		const result = await api.get('/api/academy/');
		if (result.ok && result.data.data?.length > 0) {
			academyStore.set(result.data.data[0]);
		} else {
			academyStore.set(null);
		}
	}

	let isProfileMenuOpen = false;
	let profileMenuRef;

	function toggleProfileMenu() {
		isProfileMenuOpen = !isProfileMenuOpen;
	}

	function closeProfileMenu() {
		isProfileMenuOpen = false;
	}

	function handleLogout() {
		authStore.logout();
		closeProfileMenu();
		goto('/');
	}

	function handleClickOutside(event) {
		if (profileMenuRef && !profileMenuRef.contains(event.target)) {
			closeProfileMenu();
		}
	}
</script>

<svelte:window on:click={handleClickOutside} />

<header
	class="h-16 bg-gray-50 border-b border-gray-200 flex items-center justify-between px-4 shadow-sm"
>
	<div class="flex items-center gap-3">
		<!-- 모바일 햄버거 버튼 -->
		<button
			class="lg:hidden p-2 rounded-lg text-gray-600 hover"
			on:click={() => uiStore.toggleSidebar()}
			aria-label="메뉴 열기"
		>
			<MenuIcon class="w-6 h-6" />
		</button>

		<!-- 로고 + 학원명 -->
		<div class="hidden sm:flex items-center gap-2">
			<a href="/" class="flex items-center gap-2">
				<img src="/logo.png" alt="단비데스크 로고" class="w-[1.875rem] h-[1.875rem]" />
			</a>
			{#if hasAcademy}
				<span class="text-xl font-bold text-blue-600">{academyName}</span>
			{:else if isLoggedIn}
				<a href="/dashboard/academy/create" class="text-sm text-gray-500 hover">
					학원을 등록해주세요
				</a>
			{:else}
				<span class="text-xl font-bold" style="color: #3F5264;">단비데스크</span>
			{/if}
		</div>
	</div>

	<div class="flex items-center gap-1 sm:gap-3">
		{#if isLoggedIn}
			<a
				href="/dashboard/announcements"
				class="relative p-2 text-gray-600 hover:text-gray-800 rounded-lg transition-colors"
				title="공지사항"
			>
				<svg
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					stroke-width="1.5"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
					/>
				</svg>
				{#if $hasUnread}
					<span class="absolute top-1.5 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
				{/if}
			</a>
			<a
				href="/dashboard/inquiries"
				class="relative px-3 py-1.5 text-sm font-medium text-gray-600 hover rounded-lg transition-colors"
			>
				1:1 문의
				{#if $hasUnreadInquiryReply}
					<span
						class="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full"
						aria-label="새 답변 있음"
					></span>
				{/if}
			</a>
		{/if}
		<a
			href="/faq"
			class="px-3 py-1.5 text-sm font-medium text-gray-600 hover rounded-lg transition-colors"
		>
			FAQ
		</a>
		<!-- 로그인/프로필 영역 -->
		{#if isLoggedIn}
			<div class="relative" bind:this={profileMenuRef}>
				<button
					class="flex items-center gap-2 text-gray-700 hover rounded-lg p-2"
					on:click|stopPropagation={toggleProfileMenu}
					aria-label="프로필 메뉴"
				>
					<img
						src="/images/default-profile.png"
						alt=""
						class="w-8 h-8 rounded-full object-cover"
					/>
					<svg
						class="w-4 h-4 transition-transform {isProfileMenuOpen ? 'rotate-180' : ''}"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 9l-7 7-7-7"
						/>
					</svg>
				</button>

				<!-- 드롭다운 메뉴 -->
				{#if isProfileMenuOpen}
					<div
						class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
					>
						<a
							href="/dashboard/settings/profile"
							class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover"
							on:click={closeProfileMenu}
						>
							<UsersIcon class="w-4 h-4" />
							프로필
						</a>
						{#if !isOAuthUser}
							<a
								href="/dashboard/settings/change-password"
								class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover"
								on:click={closeProfileMenu}
							>
								<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
									/>
								</svg>
								비밀번호 변경
							</a>
						{/if}
						<hr class="my-1 border-gray-200" />
						<button
							class="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover"
							on:click={handleLogout}
						>
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
								/>
							</svg>
							로그아웃
						</button>
					</div>
				{/if}
			</div>
		{:else}
			<a
				href="/auth/login"
				class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover rounded-lg"
			>
				로그인
			</a>
		{/if}
	</div>
</header>
